/* eslint max-classes-per-file: ["error", 2] */
const _ = require('lodash');
const uuid = require('uuid/v4');
const CONST = require('../../constants');
const templateDefaults = require('../../ToolsOperationsFactory/defaults/operations');

function replaceTextToolSubType(operation) {
  if (_.get(operation, 'properties.subType') === 'overlying') {
    _.set(operation, 'properties.subType', 'none');
  }
  return operation;
}

class AJsonStruct {
  constructor({ templateIdGenerator, fieldIds = [] }) {
    this._doc = {
      // eslint-disable-next-line max-len
      config:
        '<document config_ver="3"><content_settings><images><owner/><recipient/></images><embed_fonts>1</embed_fonts></content_settings><pages/></document>',
      attributes: {
        metadata: { version: '2.12.0', dpi: 72 },
        list: {},
      },
      fields: {
        metadata: { version: '2.12.0', dpi: 72 },
        list: [],
      },
      content: {
        metadata: { version: '2.12.0', dpi: 72 },
        list: [],
      },
      roles: {
        metadata: { version: '2.12.0', dpi: 72 },
        list: [],
      },
      comments: {
        metadata: { version: '2.12.0', dpi: 72 },
        list: [],
      },
    };
    this._maxFieldOrder = 0;
    this._fieldIds = fieldIds;

    if (typeof templateIdGenerator === 'function') this._templateIdGenerator = templateIdGenerator;
  }

  addPages(pages) {
    this._doc.pages = this._genMetadataStruct(pages);
  }

  addAttribute(operationProps) {
    const SECTION_ATTRIBUTES = 'attributes';
    const subType = _.get(operationProps, 'subType', false);
    const operationContent = _.get(operationProps, 'content', false);

    if (!subType || !operationContent) return;
    this._assertSection(SECTION_ATTRIBUTES, this._genMetadataStruct({}));
    this._doc[SECTION_ATTRIBUTES].list[subType] = {
      content: operationContent,
    };
  }

  addComment(operationProps) {
    const SECTION_NAME = 'comments';
    const { content: operationContent, pageId } = operationProps;

    if (!operationContent) return;
    const {
      // author, date, message, owner, replies, resolved, width, height, x, y,
      author,
      date,
      message,
      owner,
      replies,
      ...general
    } = operationContent;

    const comment = {
      author,
      date,
      message,
      owner,
      replies,
      general,
      // general: { resolved, width, height, x, y },
    };

    try {
      this._addToPage(SECTION_NAME, pageId, comment);
    } catch (err) {
      throw new Error(
        `Failed add to comment[${pageId}] :: ${JSON.stringify(operationProps)}`
      );
    }
  }

  addTemplate(operationProps) {
    const SECTION_NAME = 'fields';
    const {
      type, subType, template: { visible, ...template }, pageId,
    } = operationProps;

    if (typeof visible === 'boolean' && !visible) {
      return;
    }

    // control order
    if (!template.order) {
      this._maxFieldOrder++;
      template.order = this._maxFieldOrder;
    } else if (template.order === this._maxFieldOrder) {
      template.order++;
      this._maxFieldOrder = template.order;
    } else {
      this._maxFieldOrder = Math.max(this._maxFieldOrder, template.order);
    }

    // control ID
    if (!template.id) {
      const id = this._genTemplateId();

      if (id) {
        template.id = id;
        this._fieldIds.push(id);
      }
    }

    const fullTemplateObject = this._genFullTemplateObject({
      type,
      subType,
      ...template,
      pageId,
    });

    try {
      this._addToPage(SECTION_NAME, pageId, fullTemplateObject);
      return fullTemplateObject;
    } catch (err) {
      throw new Error(
        `Failed add to fields[${pageId}] :: ${JSON.stringify(
          fullTemplateObject
        )}`
      );
    }
  }

  addContent(operationProps) {
    const SECTION_NAME = 'content';
    const {
      content: { visible: visibleContent, ...content } = {},
      pageId,
    } = operationProps;

    const { visibleTemplate, template } = ((tpl) => {
      if (!tpl) return {};

      const { visible, ...rest } = tpl;

      return { visibleTemplate: visible, template: rest };
    })(operationProps.template);

    const isTemplateVisibleFalse = typeof visibleTemplate === 'boolean' && !visibleTemplate;
    const isContentVisibleFalse = typeof visibleContent === 'boolean' && !visibleContent;

    if (isTemplateVisibleFalse || isContentVisibleFalse) {
      return;
    }

    const contentExtras = typeof template !== 'object'
      ? {}
      : {
        linkId: template.id,
        x: template.x,
        y: template.y,
        height: content.height,
        width: content.width,
        ...(template.valign ? { valign: template.valign } : {}),
      };

    const contentFull = {
      type: operationProps.type,
      subType: operationProps.subType,
      ...content,
      ...contentExtras,
    };

    try {
      this._addToPage(SECTION_NAME, pageId, contentFull);
      return contentFull;
    } catch (err) {
      throw new Error(
        `Failed add ${SECTION_NAME}[${pageId}] ${JSON.stringify(contentFull)}`
      );
    }
  }

  toJson() {
    return this._doc;
  }

  _assertSection(name, def) {
    if (this._doc[name]) return true;
    if (!def) def = this._genMetadataStruct([]);
    this._doc[name] = def;
    return true;
  }

  _genMetadataStruct(list = [], metadata = {}) {
    return {
      metadata: {
        version: metadata.version || CONST.METADATA_DEFAULTS.VERSION,
        dpi: metadata.dpi || CONST.METADATA_DEFAULTS.DPI,
      },
      list,
    };
  }

  _addToPage(sectionName, pageId, item) {
    this._assertSection(sectionName);
    this._doc[sectionName].list = this._fillPagesTo(
      this._doc[sectionName].list,
      pageId
    );
    this._doc[sectionName].list[pageId].push(item);
    return true;
  }

  _genTemplateId() {
    if (this._templateIdGenerator) return this._templateIdGenerator(this._fieldIds);
    return uuid();
  }

  _fillPagesTo(list, num = 0) {
    const res = [...list];

    for (let i = 0; i <= num; i++) {
      if (!res[i]) res[i] = [];
    }
    return res;
  }

  _genFullTemplateObject(template = {}) {
    return {
      ...this._getDefTemplate(template.type),
      ...template,
      ...this._getTemplateExtras(template),
    };
  }

  _getDefTemplate(type) {
    const defaults = _.get(templateDefaults, type, {});

    return defaults;
  }

  _getTemplateExtras(template) {
    // https://pdffiller.atlassian.net/wiki/spaces/PD/pages/58984966/Constructor
    // if (template.type === 'text' && template.subType === 'date') {
    //   return {
    //     initial: getInitialDate(template),
    //     subType: `current+${template.format || 'MM/dd/yyyy'}`,
    //   };
    // }

    return template;
  }
}

module.exports = class OperationsToDocumentConverter {
  convert(operations, options = {}) {
    const { forEachOperation, templateIdGenerator } = options;

    // collect exist ids
    const fieldIds = operations.reduce((allIds, op) => {
      const id = _.get(op, 'properties.template.id');

      if (id) allIds.push(id);
      return allIds;
    }, []);

    const docStruct = new AJsonStruct({
      templateIdGenerator,
      fieldIds,
    });

    operations.forEach((operation) => {
      if (_.get(operation, 'properties.type') === 'defaults') return;

      const TYPE_PAGE = 'pages';
      const TYPE_ATTRIBUTE = 'attributes';
      const TYPE_COMMENT = 'comment';

      // brefore apply operation transforms
      operation = this._transformOperation(operation);
      if (typeof forEachOperation === 'function') forEachOperation(operation);
      if (!operation) return;

      const operationType = _.get(operation, 'properties.type', false);

      // pages
      const isPageOperation = operationType === TYPE_PAGE;

      if (isPageOperation) {
        return docStruct.addPages(_.get(operation, 'properties.pages', []));
      }

      // attributes
      const isAttributeOperation = operationType === TYPE_ATTRIBUTE;

      if (isAttributeOperation) {
        return docStruct.addAttribute(operation.properties);
      }

      // comment
      const isCommentOperation = operationType === TYPE_COMMENT;

      if (isCommentOperation) {
        return docStruct.addComment(operation.properties);
      }

      const isContentOperation = !!_.get(
        operation,
        'properties.content',
        false
      );
      const isTemplateOperation = !!_.get(
        operation,
        'properties.template',
        false
      );

      // template
      let newTemplate;

      if (isTemplateOperation) {
        newTemplate = docStruct.addTemplate(
          operation.properties
        );
      }

      // content
      if (isContentOperation) {
        docStruct.addContent(
          {
            ...operation.properties,
            ...(newTemplate ? { template: newTemplate } : {}),
          }
        );
      }
    });

    return docStruct.toJson();
  }

  _transformOperation(operation, options) {
    const transformChain = [
      replaceTextToolSubType,
    ];

    transformChain.forEach((fn) => {
      if (operation) operation = fn(operation, options);
    });
    return operation;
  }
};
